// Write: whatever you play or sing becomes notes, then tab (2026-10-09).
// One line at a time (single notes, the way the pitch tracker hears).
// Strummed chords need a polyphonic model (Basic Pitch) and come later.

import { PitchResult } from './pitchDetection';

export interface PitchSample { t: number; p: PitchResult | null; }

export interface WrittenNote {
  midi: number;        // the nearest key
  hz: number;          // median frequency actually played
  cents: number;       // how far from that key, median
  startMs: number;
  durMs: number;
}

export interface TabNote extends WrittenNote { string: number; fret: number; } // string 0 = low E

const LETTERS = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const FLATS = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];
export const letterOf = (midi: number) => LETTERS[((midi % 12) + 12) % 12];
export const flatOf = (midi: number) => FLATS[((midi % 12) + 12) % 12];
export const octaveOf = (midi: number) => Math.floor(midi / 12) - 1;

const MIN_CLARITY = 0.85;
const MIN_NOTE_MS = 70;    // shorter than this is a slip, not a note
const SETTLE_MS = 45;      // a new key must hold this long before a new note starts
const GAP_MS = 90;         // silence longer than this ends a note

const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; };

/** Cut a pitch trace into notes. A glide becomes the notes it parks on. */
export function segmentNotes(samples: PitchSample[]): WrittenNote[] {
  const notes: WrittenNote[] = [];
  let cur: { midi: number; start: number; last: number; hz: number[]; cents: number[] } | null = null;
  let cand: { midi: number; since: number } | null = null;

  const close = () => {
    if (cur && cur.last - cur.start >= MIN_NOTE_MS) {
      const hz = median(cur.hz);
      notes.push({ midi: cur.midi, hz: Math.round(hz * 10) / 10, cents: Math.round(median(cur.cents)), startMs: cur.start, durMs: Math.round(cur.last - cur.start) });
    }
    cur = null;
  };

  for (const s of samples) {
    const p = s.p;
    const ok = p && p.hz > 0 && p.clarity >= MIN_CLARITY;
    if (!ok) {
      if (cur && s.t - cur.last > GAP_MS) close();
      cand = null;
      continue;
    }
    const exact = 12 * Math.log2(p!.hz / 440) + 69;
    const midi = Math.round(exact);
    const cents = (exact - midi) * 100;
    if (!cur) { cur = { midi, start: s.t, last: s.t, hz: [p!.hz], cents: [cents] }; cand = null; continue; }
    if (midi === cur.midi) { cur.last = s.t; cur.hz.push(p!.hz); cur.cents.push(cents); cand = null; continue; }
    // a different key: wait to see if it settles there
    if (!cand || cand.midi !== midi) cand = { midi, since: s.t };
    if (s.t - cand.since >= SETTLE_MS) {
      close();
      cur = { midi, start: cand.since, last: s.t, hz: [p!.hz], cents: [cents] };
      cand = null;
    }
  }
  close();
  return notes;
}

/** Standard tuning, low to high: E2 A2 D3 G3 B3 E4. */
export const STANDARD = [40, 45, 50, 55, 59, 64];
export const STRING_NAMES = ['E', 'A', 'D', 'G', 'B', 'e'];
const MAX_FRET = 20;

/**
 * Place each note on a string and fret, keeping the hand where it is when it
 * can. Every place a note could live is scored by how far the hand moves from
 * the last note, with a nudge toward the open end of the neck; the cheapest
 * path through the whole line wins.
 */
export function toTab(notes: WrittenNote[], tuning: number[] = STANDARD): TabNote[] {
  if (notes.length === 0) return [];
  const options = notes.map(n => {
    const o: { string: number; fret: number }[] = [];
    tuning.forEach((open, s) => { const f = n.midi - open; if (f >= 0 && f <= MAX_FRET) o.push({ string: s, fret: f }); });
    return o;
  });
  const placeCost = (f: number) => (f === 0 ? 0 : 0.15 * f);
  const moveCost = (a: { fret: number; string: number }, b: { fret: number; string: number }) => {
    if (b.fret === 0 || a.fret === 0) return 0.5 * Math.abs(a.string - b.string) * 0.3;
    return Math.abs(a.fret - b.fret) + 0.3 * Math.abs(a.string - b.string);
  };

  const cost: number[][] = [], from: number[][] = [];
  options.forEach((opts, i) => {
    cost[i] = []; from[i] = [];
    opts.forEach((o, j) => {
      if (i === 0 || options[i - 1].length === 0) { cost[i][j] = placeCost(o.fret); from[i][j] = -1; return; }
      let best = Infinity, arg = -1;
      options[i - 1].forEach((prev, k) => { const c = cost[i - 1][k] + moveCost(prev, o); if (c < best) { best = c; arg = k; } });
      cost[i][j] = best + placeCost(o.fret); from[i][j] = arg;
    });
  });

  // walk back from the cheapest last place; notes off the neck get no place
  const out: TabNote[] = new Array(notes.length);
  let i = notes.length - 1;
  while (i >= 0 && options[i].length === 0) { out[i] = { ...notes[i], string: -1, fret: -1 }; i--; }
  if (i >= 0) {
    let j = cost[i].indexOf(Math.min(...cost[i]));
    for (; i >= 0; i--) {
      if (options[i].length === 0) { out[i] = { ...notes[i], string: -1, fret: -1 }; j = -1; continue; }
      if (j < 0) j = cost[i].indexOf(Math.min(...cost[i]));
      out[i] = { ...notes[i], ...options[i][j] };
      j = from[i][j];
    }
  }
  return out;
}

/** Plain text tab, high e on top, ready to copy or save. Space between notes follows time. */
export function tabText(tab: TabNote[], tuning: number[] = STANDARD): string {
  const lines = tuning.map((_, s) => STRING_NAMES[s].padEnd(2) + '|');
  const names = '   ';
  let nameLine = names;
  let prevEnd = tab.length ? tab[0].startMs : 0;
  for (const n of tab) {
    const gap = n.startMs - prevEnd;
    const pad = gap > 600 ? 3 : gap > 250 ? 2 : 1;
    const label = n.fret < 0 ? '?' : String(n.fret);
    const w = Math.max(label.length, letterOf(n.midi).length);
    for (let s = 0; s < lines.length; s++) lines[s] += '-'.repeat(pad) + (s === n.string ? label.padEnd(w, '-') : '-'.repeat(w));
    nameLine += ' '.repeat(pad) + letterOf(n.midi).padEnd(w);
    prevEnd = n.startMs + n.durMs;
  }
  for (let s = 0; s < lines.length; s++) lines[s] += '--|';
  return [nameLine.trimEnd(), ...lines.reverse()].join('\n');
}
